import { useContent } from '../../contexts/ContentContext'
import FullHeightSection from '../ui/FullHeightSection'
import SectionLabel from '../ui/SectionLabel'
import CtaButton from '../ui/CtaButton'
import LazyImage from '../ui/LazyImage'

/**
 * CtaImageSection — CTA section with image and typography tokens
 *
 * Design Tokens (Single Source of Truth):
 * - Font-family: font-secondary (headlines), font-primary (body)
 * - Label: text-label (15px) + color-label
 * - Headline: typography-hero-large (48px) + color-heading
 * - Subline: text-body-lg (20px) + color-body
 * - Button: CtaButton with primary variant
 *
 * Layout:
 * - Image on left (max-w-[280px] on mobile, max-w-[320px] on desktop)
 * - Text left-aligned on right
 * - Button underneath text
 * - Full viewport height using FullHeightSection utility
 *
 * CRITICAL PRESERVED:
 * - Section with id="simple-cta" (scroll target)
 * - Content from plr-de.js (no hardcoding)
 * - Reuses existing components (CtaButton, SectionLabel, LazyImage)
 */
export default function CtaImageSection() {
  const { ctaImage } = useContent()

  return (
    <FullHeightSection data-section="CtaImageSection" id="simple-cta" data-testid="simple-cta-section">
      <div className="grid sm:grid-cols-[240px_1fr] md:grid-cols-[288px_1fr] card-grid-gap-sm md:card-grid-gap-lg items-center">
        {/* Image - Left column */}
        <div className="flex justify-center md:justify-start">
          <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-72 lg:h-72 rounded-full overflow-hidden flex-shrink-0 border border-color-primary">
            <LazyImage
              src={ctaImage.imageUrl}
              alt={ctaImage.imageAlt}
              className="w-full h-full object-cover object-top scale-[1.03]"
            />
          </div>
        </div>

        {/* Text - centered on mobile, left-aligned on desktop */}
        <div className="text-center sm:text-left">
          <SectionLabel text={ctaImage.label} />

          <h2 className="typography-hero-large leading-tight heading-body-spacing">
            {ctaImage.headline}
          </h2>

          <p className="typography-body-lg leading-relaxed">
            {ctaImage.subline}
          </p>

          <div className="margin-top-md flex justify-center sm:justify-start">
            <CtaButton label={ctaImage.cta} variant="primary" className="py-4 px-12" />
          </div>
        </div>
      </div>
    </FullHeightSection>
  )
}
